// File: src/App.js
// Description: Main App component with routing, theme, and authentication setup for PropVantage AI
// Version: 1.6 - ENHANCED for Phase 1 Analytics Implementation + INVITATION ROUTE ADDED
// Location: src/App.js

import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, Box, Typography } from '@mui/material';
import { SnackbarProvider } from 'notistack';

// Theme and Context Providers
import theme from './theme';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ChatProvider } from './context/ChatContext';
import { CoachMarkProvider, FLOWS } from './components/onboarding';
import { ProjectProvider } from './context/ProjectContext';
import { WorkspaceProvider } from './context/WorkspaceContext';

// Layout Components
import AuthLayout from './components/layout/AuthLayout';
import DashboardLayout from './components/layout/DashboardLayout';
import ChannelPartnerLayout from './components/layout/ChannelPartnerLayout';

// Auth Pages - UNCHANGED
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
const InviteAcceptancePage = React.lazy(() => import('./pages/auth/InviteAcceptancePage'));
const RegisterChoicePage = React.lazy(() => import('./pages/auth/RegisterChoicePage'));
const ChannelPartnerRegisterPage = React.lazy(() => import('./pages/auth/ChannelPartnerRegisterPage'));

// Pitch landing page — public, outside auth flow (in-person pitches only).
// Not linked in navigation. Reachable only by direct URL.
const PitchLandingPage = React.lazy(() => import('./pages/pitch/PitchLandingPage'));
const MicroMittiPitchPage = React.lazy(() => import('./pages/pitch/MicroMittiPitchPage'));

// Public report page — email-gated, fully public (no auth wrapper).
const PublicReportPage = React.lazy(() => import('./pages/public/PublicReportPage'));

// Dashboard Pages (Lazy loaded for performance) - UNCHANGED
const BusinessHeadDashboard = React.lazy(() => import('./pages/dashboard/BusinessHeadDashboard'));
const SalesExecutiveDashboard = React.lazy(() => import('./pages/dashboard/SalesExecutiveDashboard'));
const SalesManagerDashboard = React.lazy(() => import('./pages/dashboard/SalesManagerDashboard'));
const FinanceHeadDashboard = React.lazy(() => import('./pages/dashboard/FinanceHeadDashboard'));
const ProjectDirectorDashboard = React.lazy(() => import('./pages/dashboard/ProjectDirectorDashboard'));

// Personalized Workspace (default landing) - new
const WorkspacePage = React.lazy(() => import('./pages/workspace/WorkspacePage'));
const HomePage = React.lazy(() => import('./pages/home/HomePage'));

// Support Tickets Pages
const TicketsListPage = React.lazy(() => import('./pages/support/TicketsListPage'));
const TicketDetailPage = React.lazy(() => import('./pages/support/TicketDetailPage'));
const PublicTicketPage = React.lazy(() => import('./pages/public/PublicTicketPage'));

// Project Management Pages - UNCHANGED
const ProjectsListPage = React.lazy(() => import('./pages/projects/ProjectsListPage'));
const ProjectDetailPage = React.lazy(() => import('./pages/projects/ProjectDetailPage'));
const TowerDetailPage = React.lazy(() => import('./pages/projects/TowerDetailPage'));
const UnitDetailPage = React.lazy(() => import('./pages/projects/UnitDetailPage'));
const CreateProjectPage = React.lazy(() => import('./pages/projects/CreateProjectPage'));
const CreateTowerPage = React.lazy(() => import('./pages/projects/CreateTowerPage'));
const CreateUnitPage = React.lazy(() => import('./pages/projects/CreateUnitPage'));

// Edit Pages - Complete Edit/Delete Functionality - UNCHANGED
const EditProjectPage = React.lazy(() => import('./pages/projects/EditProjectPage'));
const EditTowerPage = React.lazy(() => import('./pages/projects/EditTowerPage'));
const EditUnitPage = React.lazy(() => import('./pages/projects/EditUnitPage'));

// Lead Management Pages - UNCHANGED
const LeadsListPage = React.lazy(() => import('./pages/leads/LeadsListPage'));
const LeadDetailPage = React.lazy(() => import('./pages/leads/LeadDetailPage'));
const CreateLeadPage = React.lazy(() => import('./pages/leads/CreateLeadPage'));
const EditLeadPage = React.lazy(() => import('./pages/leads/EditLeadPage'));
const LeadsPipelinePage = React.lazy(() => import('./pages/leads/LeadsPipelinePage'));

// Sales Management Pages - UNCHANGED
const SalesListPage = React.lazy(() => import('./pages/sales/SalesListPage'));
const SaleDetailPage = React.lazy(() => import('./pages/sales/SaleDetailPage'));
const CreateSalePage = React.lazy(() => import('./pages/sales/CreateSalePage'));
const EditSalePage = React.lazy(() => import('./pages/sales/EditSalePage'));
const PaymentPlanManagementPage = React.lazy(() => import('./pages/payments/PaymentPlanManagementPage'));
const PaymentPlanPage = React.lazy(() => import('./pages/payments/PaymentPlanPage'));

const SalesReportsPage = React.lazy(() => import('./pages/sales/SalesReportsPage'));
const SalesPipelinePage = React.lazy(() => import('./pages/sales/SalesPipelinePage'));

// Commission Management Pages - UNCHANGED

// Payment Management Pages - UNCHANGED
const PaymentDashboardPage = React.lazy(() => import('./pages/payments/PaymentDashboardPage'));
const DueTodayPage = React.lazy(() => import('./pages/payments/DueTodayPage'));
const OverduePaymentsPage = React.lazy(() => import('./pages/payments/OverduePaymentsPage'));
const CollectionPerformancePage = React.lazy(() => import('./pages/payments/CollectionPerformancePage'));
const PaymentReportsPage = React.lazy(() => import('./pages/payments/PaymentReportsPage'));
const RecordPaymentPage = React.lazy(() => import('./pages/payments/RecordPaymentPage'));

// =============================================================================
// ANALYTICS PAGES - EXISTING + NEW FOR PHASE 1
// =============================================================================

// EXISTING Analytics Pages - UNCHANGED
const AnalyticsDashboard = React.lazy(() => import('./pages/analytics/AnalyticsDashboard'));
const SalesAnalytics = React.lazy(() => import('./pages/analytics/SalesAnalytics'));
const RevenueAnalytics = React.lazy(() => import('./pages/analytics/RevenueAnalytics'));
const LeadAnalytics = React.lazy(() => import('./pages/analytics/LeadAnalytics'));

// NEW: Phase 1 Analytics Pages - ENHANCED ANALYTICS CAPABILITIES
const BudgetVsActualDashboard = React.lazy(() => import('./pages/analytics/BudgetVsActualDashboard'));
const RealTimeFinancialDashboard = React.lazy(() => import('./pages/analytics/RealTimeFinancialDashboard'));
const BudgetVarianceDashboard = React.lazy(() => import('./pages/analytics/BudgetVarianceDashboard'));


// Phase 2 - Predictive Analytics & Dynamic Pricing
const PredictiveAnalyticsPage = React.lazy(() => import('./pages/analytics/PredictiveAnalyticsPage'));

// Leadership Dashboard
const LeadershipDashboardPage = React.lazy(() => import('./pages/analytics/LeadershipDashboardPage'));
const DynamicPricingPage = React.lazy(() => import('./pages/pricing/DynamicPricingPage'));

// Invoice Management Pages - UNCHANGED
const InvoiceListPage = React.lazy(() => import('./pages/sales/InvoiceListPage'));
const InvoiceDetailPage = React.lazy(() => import('./pages/sales/InvoiceDetailPage'));
const GenerateInvoicePage = React.lazy(() => import('./pages/sales/GenerateInvoicePage'));

// Settings and Profile Pages - UNCHANGED
const ProfilePage = React.lazy(() => import('./pages/profile/ProfilePage'));
const SettingsPage = React.lazy(() => import('./pages/settings/SettingsPage'));
const UserManagementPage = React.lazy(() => import('./pages/settings/UserManagementPage'));
const SupportSettingsPage = React.lazy(() => import('./pages/settings/SupportSettingsPage'));
const VoiceAgentSettingsPage = React.lazy(() => import('./pages/settings/VoiceAgentSettingsPage'));
const ImportDataPage = React.lazy(() => import('./pages/settings/ImportDataPage'));
const SalesIntelligencePage = React.lazy(() => import('./pages/analytics/SalesIntelligencePage'));
const RolesListPage = React.lazy(() => import('./pages/settings/RolesListPage'));
const CreateEditRolePage = React.lazy(() => import('./pages/settings/CreateEditRolePage'));
const RoleDetailPage = React.lazy(() => import('./pages/settings/RoleDetailPage'));
const OrgHierarchyPage = React.lazy(() => import('./pages/settings/OrgHierarchyPage'));
const ProjectAccessPage = React.lazy(() => import('./pages/settings/ProjectAccessPage'));

// Notification Pages
const NotificationsPage = React.lazy(() => import('./pages/notifications/NotificationsPage'));
const NotificationPreferencesPage = React.lazy(() => import('./pages/settings/NotificationPreferencesPage'));

// SP5 — CP analytics + insights pages
const CpInsightsPage                  = React.lazy(() => import('./pages/cp-portal/CpInsightsPage'));
const CommissionDashboardPage         = React.lazy(() => import('./pages/cp-portal/CommissionDashboardPage'));
const ReconciliationDashboardPage     = React.lazy(() => import('./pages/cp-portal/ReconciliationDashboardPage'));
const DeveloperPerformanceDetailPage  = React.lazy(() => import('./pages/cp-portal/DeveloperPerformanceDetailPage'));
// SP5 — dev-side analytics pages
const ChannelPartnerScorecardPage = React.lazy(() => import('./pages/analytics/ChannelPartnerScorecardPage'));
const CommissionPayoutsPage       = React.lazy(() => import('./pages/analytics/CommissionPayoutsPage'));
const LeadQualityPage             = React.lazy(() => import('./pages/analytics/LeadQualityPage'));

// Task Management Pages
const MyTasksPage = React.lazy(() => import('./pages/tasks/MyTasksPage'));
const TaskListPage = React.lazy(() => import('./pages/tasks/TaskListPage'));
const TaskKanbanPage = React.lazy(() => import('./pages/tasks/TaskKanbanPage'));
const TaskDetailPage = React.lazy(() => import('./pages/tasks/TaskDetailPage'));
const CreateEditTaskPage = React.lazy(() => import('./pages/tasks/CreateEditTaskPage'));
const TeamTasksPage = React.lazy(() => import('./pages/tasks/TeamTasksPage'));
const TaskAnalyticsPage = React.lazy(() => import('./pages/tasks/TaskAnalyticsPage'));
const TaskTemplatesPage = React.lazy(() => import('./pages/tasks/TaskTemplatesPage'));
const CreateEditTemplatePage = React.lazy(() => import('./pages/tasks/CreateEditTemplatePage'));

// Approval Management Pages
const ApprovalsDashboardPage = React.lazy(() => import('./pages/approvals/ApprovalsDashboardPage'));
const ApprovalDetailPage = React.lazy(() => import('./pages/approvals/ApprovalDetailPage'));
const ApprovalPoliciesPage = React.lazy(() => import('./pages/approvals/ApprovalPoliciesPage'));

// Competitive Analysis Pages
const CADashboardPage = React.lazy(() => import('./pages/competitive-analysis/CADashboardPage'));
const CompetitorListPage = React.lazy(() => import('./pages/competitive-analysis/CompetitorListPage'));
const CompetitorFormPage = React.lazy(() => import('./pages/competitive-analysis/CompetitorFormPage'));
const CompetitorDetailPage = React.lazy(() => import('./pages/competitive-analysis/CompetitorDetailPage'));
const AIResearchPage = React.lazy(() => import('./pages/competitive-analysis/AIResearchPage'));
const CSVImportPage = React.lazy(() => import('./pages/competitive-analysis/CSVImportPage'));
const MarketOverviewPage = React.lazy(() => import('./pages/competitive-analysis/MarketOverviewPage'));
const MarketTrendsPage = React.lazy(() => import('./pages/competitive-analysis/MarketTrendsPage'));
const DemandSupplyPage = React.lazy(() => import('./pages/competitive-analysis/DemandSupplyPage'));
const AIAnalysisPage = React.lazy(() => import('./pages/competitive-analysis/AIAnalysisPage'));
const DataProvidersPage = React.lazy(() => import('./pages/competitive-analysis/DataProvidersPage'));
const CompetitivePerformancePage = React.lazy(() => import('./pages/competitive-analysis/CompetitivePerformancePage'));

// Portfolio Pages
const PortfolioProfilePage = React.lazy(() => import('./pages/portfolio/PortfolioProfilePage'));
const PortfolioPreviewPage = React.lazy(() => import('./pages/portfolio/PortfolioPreviewPage'));

// CP Portal Pages (for channel_partner org users)
const CpPortalDashboardPage = React.lazy(() => import('./pages/cp-portal/CpPortalDashboardPage'));
const CpPortalTeamPage = React.lazy(() => import('./pages/cp-portal/CpPortalTeamPage'));
const CpPortalProfilePage = React.lazy(() => import('./pages/cp-portal/CpPortalProfilePage'));
const CpMarketplacePage = React.lazy(() => import('./pages/cp-portal/CpMarketplacePage'));
const CpPartnershipsPage = React.lazy(() => import('./pages/cp-portal/CpPartnershipsPage'));
// SP4 — CP-side Prospect pipeline + Off-platform developers
const ProspectsListPage = React.lazy(() => import('./pages/cp-portal/ProspectsListPage'));
const ProspectDetailPage = React.lazy(() => import('./pages/cp-portal/ProspectDetailPage'));
const ExternalDevelopersListPage = React.lazy(() => import('./pages/cp-portal/ExternalDevelopersListPage'));
// SP4 — Developer-side Lead Registrations queue
const LeadRegistrationsPage = React.lazy(() => import('./pages/leads/LeadRegistrationsPage'));

// Channel Partners
const ChannelPartnerListPage = React.lazy(() => import('./pages/channel-partners/ChannelPartnerListPage'));
const ChannelPartnerFormPage = React.lazy(() => import('./pages/channel-partners/ChannelPartnerFormPage'));
const CommissionRuleListPage = React.lazy(() => import('./pages/channel-partners/CommissionRuleListPage'));
const CommissionRuleFormPage = React.lazy(() => import('./pages/channel-partners/CommissionRuleFormPage'));
const CommissionRecordListPage = React.lazy(() => import('./pages/channel-partners/CommissionRecordListPage'));
const ChannelPartnerDashboardPage = React.lazy(() => import('./pages/channel-partners/ChannelPartnerDashboardPage'));
const DeveloperPartnershipsPage = React.lazy(() => import('./pages/channel-partners/DeveloperPartnershipsPage'));

// Chat
const ChatPage = React.lazy(() => import('./pages/chat/ChatPage'));

// Reports (Leadership Report Builder)
const ReportTemplateListPage = React.lazy(() => import('./pages/reports/ReportTemplateListPage'));
const ReportAgentPage = React.lazy(() => import('./pages/reports/ReportAgentPage'));
const ReportInstanceListPage = React.lazy(() => import('./pages/reports/ReportInstanceListPage'));
const ReportInstanceAnalyticsPage = React.lazy(() => import('./pages/reports/ReportInstanceAnalyticsPage'));
const ReportReviewPage = React.lazy(() => import('./pages/reports/ReportReviewPage'));

// People & Performance Pages
const MyPerformancePage    = React.lazy(() => import('./pages/people/MyPerformancePage'));
const TeamPerformancePage  = React.lazy(() => import('./pages/people/TeamPerformancePage'));
const OrgPerformancePage   = React.lazy(() => import('./pages/people/OrgPerformancePage'));

// Error Pages - UNCHANGED
const NotFoundPage = React.lazy(() => import('./pages/error/NotFoundPage'));
const UnauthorizedPage = React.lazy(() => import('./pages/error/UnauthorizedPage'));

// =============================================================================
// LOADING COMPONENT - ENHANCED FOR PHASE 1
// =============================================================================

/**
 * Enhanced Loading Component with contextual messages for different sections
 * ENHANCED: Added specific loading messages for analytics components
 */
const LoadingFallback = ({ message = 'Loading...', section = null }) => {
  const displayMessage = section === 'analytics' ? 'Loading analytics dashboard...' :
    section === 'budget' ? 'Loading financial data...' :
    section === 'realtime' ? 'Connecting to real-time data...' :
    section === 'invitation' ? 'Loading invitation...' :
    message;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        gap: 2.5,
        animation: 'fadeIn 0.3s ease',
      }}
    >
      {/* Animated logo placeholder */}
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '12px',
          background: (t) => `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.primary.dark})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      >
        <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.25rem' }}>P</Typography>
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          {displayMessage}
        </Typography>
        {/* Subtle progress bar */}
        <Box
          sx={{
            width: 120,
            height: 3,
            borderRadius: 2,
            bgcolor: 'grey.200',
            overflow: 'hidden',
            mt: 1.5,
            mx: 'auto',
          }}
        >
          <Box
            sx={{
              width: '40%',
              height: '100%',
              borderRadius: 2,
              bgcolor: 'primary.main',
              animation: 'loading-slide 1.2s ease-in-out infinite',
              '@keyframes loading-slide': {
                '0%': { transform: 'translateX(-100%)' },
                '100%': { transform: 'translateX(350%)' },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

// =============================================================================
// PROTECTED ROUTE COMPONENT - UNCHANGED
// =============================================================================

const ProtectedRoute = ({ children, requiredPermission, fallback = <UnauthorizedPage /> }) => {
  const { isAuthenticated, isLoading, hasPermission, canAccess, checkPerm } = useAuth();

  if (isLoading) {
    return <LoadingFallback message="Authenticating..." />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Check specific permission if provided
  if (requiredPermission) {
    if (typeof requiredPermission === 'string') {
      // New module:action format (e.g. "projects:view")
      if (requiredPermission.includes(':') && checkPerm) {
        if (!checkPerm(requiredPermission)) {
          return fallback;
        }
      } else {
        // Legacy group format (e.g. "ADMIN", "SALES")
        if (!hasPermission(requiredPermission)) {
          return fallback;
        }
      }
    } else if (typeof requiredPermission === 'function') {
      if (!requiredPermission(canAccess)) {
        return fallback;
      }
    }
  }

  return children;
};

// =============================================================================
// CHANNEL PARTNER ROUTE COMPONENT
// =============================================================================

// A CP-only route wrapper: authenticated AND org type is channel_partner.
const ChannelPartnerRoute = ({ children }) => {
  const { isAuthenticated, isLoading, isChannelPartnerOrg } = useAuth();
  if (isLoading) return <LoadingFallback message="Authenticating..." />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isChannelPartnerOrg) return <Navigate to="/workspace" replace />;
  return children;
};

// DashboardRouter removed — /dashboard now redirects to /workspace?view=standard.
// Role-dashboard selection lives in src/pages/workspace/RoleDashboard.js.

// =============================================================================
// PUBLIC ROUTE COMPONENT - UNCHANGED
// =============================================================================

const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingFallback message="Checking authentication..." />;
  }

  if (isAuthenticated) {
    return <Navigate to="/workspace" replace />;
  }

  return children;
};

// =============================================================================
// MAIN APP ROUTES COMPONENT - ENHANCED FOR INVITATION ROUTES
// =============================================================================

const AppRoutes = () => {
  return (
    <Routes>
      {/* ========================================= */}
      {/* PITCH LANDING PAGES — fully public, no PublicRoute wrapper        */}
      {/* so they render regardless of auth state. Not linked anywhere.    */}
      {/* ========================================= */}
      <Route path="/pitch/hubtown" element={
        <Suspense fallback={<LoadingFallback section="pitch" message="Loading..." />}>
          <PitchLandingPage />
        </Suspense>
      } />

      <Route path="/pitch/micromitti" element={
        <Suspense fallback={<LoadingFallback section="pitch" message="Loading..." />}>
          <MicroMittiPitchPage />
        </Suspense>
      } />

      {/* PUBLIC SHARED REPORT — fully public, email-gated inside the page */}
      <Route path="/r/:slug" element={
        <Suspense fallback={<LoadingFallback section="report" message="Loading report..." />}>
          <PublicReportPage />
        </Suspense>
      } />

      {/* PUBLIC TICKET STATUS — fully public, open link */}
      <Route path="/t/:token" element={
        <Suspense fallback={<LoadingFallback section="ticket" message="Loading ticket..." />}>
          <PublicTicketPage />
        </Suspense>
      } />

      {/* ========================================= */}
      {/* PUBLIC ROUTES - ENHANCED WITH INVITATION */}
      {/* ========================================= */}

      <Route path="/login" element={
        <PublicRoute>
          <AuthLayout>
            <LoginPage />
          </AuthLayout>
        </PublicRoute>
      } />
      
      <Route path="/register" element={
        <PublicRoute>
          <AuthLayout>
            <Suspense fallback={<LoadingFallback message="Loading..." />}>
              <RegisterChoicePage />
            </Suspense>
          </AuthLayout>
        </PublicRoute>
      } />

      <Route path="/register/developer" element={
        <PublicRoute>
          <AuthLayout wide>
            <RegisterPage />
          </AuthLayout>
        </PublicRoute>
      } />

      <Route path="/register/channel-partner" element={
        <PublicRoute>
          <AuthLayout wide>
            <Suspense fallback={<LoadingFallback message="Loading..." />}>
              <ChannelPartnerRegisterPage />
            </Suspense>
          </AuthLayout>
        </PublicRoute>
      } />
      
      <Route path="/forgot-password" element={
        <PublicRoute>
          <AuthLayout>
            <ForgotPasswordPage />
          </AuthLayout>
        </PublicRoute>
      } />
      
      <Route path="/reset-password" element={
        <PublicRoute>
          <AuthLayout>
            <ResetPasswordPage />
          </AuthLayout>
        </PublicRoute>
      } />

      {/* ========================================= */}
      {/* INVITATION ROUTES - NEW & CRITICAL */}
      {/* ========================================= */}

      {/* Invitation Acceptance Route - PUBLIC (no auth required) */}
      <Route path="/invite/:userId" element={
        <PublicRoute>
          <Suspense fallback={<LoadingFallback section="invitation" message="Loading invitation..." />}>
            <InviteAcceptancePage />
          </Suspense>
        </PublicRoute>
      } />

      {/* Alternative invitation route format for backwards compatibility */}
      <Route path="/invitation/:userId" element={
        <Navigate to="/invite/:userId" replace />
      } />

      {/* ========================================= */}
      {/* PERSONALIZED WORKSPACE (default landing)  */}
      {/* ========================================= */}

      <Route path="/workspace" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading your workspace..." />}>
              <WorkspacePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* SUPPORT TICKETS                           */}
      {/* ========================================= */}

      <Route path="/support" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading tickets..." />}>
              <TicketsListPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/support/:id" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading ticket..." />}>
              <TicketDetailPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* PEOPLE & PERFORMANCE                      */}
      {/* ========================================= */}

      <Route path="/people/me" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading performance..." />}>
              <MyPerformancePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/people/team" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading team performance..." />}>
              <TeamPerformancePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/people/org" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading org performance..." />}>
              <OrgPerformancePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* DASHBOARD ROUTE — redirect to workspace Standard mode */}
      {/* ========================================= */}

      <Route path="/dashboard" element={<Navigate to="/workspace?view=standard" replace />} />

      {/* The 5 role dashboards — reachable directly (nav "Dashboards" group). */}
      <Route path="/dashboards/business-head" element={
        <ProtectedRoute><DashboardLayout>
          <Suspense fallback={<LoadingFallback />}><BusinessHeadDashboard /></Suspense>
        </DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboards/project-director" element={
        <ProtectedRoute><DashboardLayout>
          <Suspense fallback={<LoadingFallback />}><ProjectDirectorDashboard /></Suspense>
        </DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboards/sales-manager" element={
        <ProtectedRoute><DashboardLayout>
          <Suspense fallback={<LoadingFallback />}><SalesManagerDashboard /></Suspense>
        </DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboards/finance-head" element={
        <ProtectedRoute><DashboardLayout>
          <Suspense fallback={<LoadingFallback />}><FinanceHeadDashboard /></Suspense>
        </DashboardLayout></ProtectedRoute>
      } />
      <Route path="/dashboards/sales-executive" element={
        <ProtectedRoute><DashboardLayout>
          <Suspense fallback={<LoadingFallback />}><SalesExecutiveDashboard /></Suspense>
        </DashboardLayout></ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* CHANNEL PARTNER PORTAL ROUTES             */}
      {/* ========================================= */}

      <Route path="/partner/dashboard" element={
        <ChannelPartnerRoute><ChannelPartnerLayout>
          <Suspense fallback={<LoadingFallback />}><CpPortalDashboardPage /></Suspense>
        </ChannelPartnerLayout></ChannelPartnerRoute>
      } />
      <Route path="/partner/team" element={
        <ChannelPartnerRoute><ChannelPartnerLayout>
          <Suspense fallback={<LoadingFallback />}><CpPortalTeamPage /></Suspense>
        </ChannelPartnerLayout></ChannelPartnerRoute>
      } />
      <Route path="/partner/profile" element={
        <ChannelPartnerRoute><ChannelPartnerLayout>
          <Suspense fallback={<LoadingFallback />}><CpPortalProfilePage /></Suspense>
        </ChannelPartnerLayout></ChannelPartnerRoute>
      } />
      <Route path="/partner/marketplace" element={
        <ChannelPartnerRoute><ChannelPartnerLayout>
          <Suspense fallback={<LoadingFallback />}><CpMarketplacePage /></Suspense>
        </ChannelPartnerLayout></ChannelPartnerRoute>
      } />
      <Route path="/partner/partnerships" element={
        <ChannelPartnerRoute><ChannelPartnerLayout>
          <Suspense fallback={<LoadingFallback />}><CpPartnershipsPage /></Suspense>
        </ChannelPartnerLayout></ChannelPartnerRoute>
      } />
      {/* SP4 — CP Prospect routes (list MUST be declared before :id) */}
      <Route path="/partner/prospects" element={
        <ChannelPartnerRoute><ChannelPartnerLayout>
          <Suspense fallback={<LoadingFallback />}><ProspectsListPage /></Suspense>
        </ChannelPartnerLayout></ChannelPartnerRoute>
      } />
      <Route path="/partner/prospects/:id" element={
        <ChannelPartnerRoute><ChannelPartnerLayout>
          <Suspense fallback={<LoadingFallback />}><ProspectDetailPage /></Suspense>
        </ChannelPartnerLayout></ChannelPartnerRoute>
      } />
      {/* SP4 — CP Off-Platform Developers */}
      <Route path="/partner/external-developers" element={
        <ChannelPartnerRoute><ChannelPartnerLayout>
          <Suspense fallback={<LoadingFallback />}><ExternalDevelopersListPage /></Suspense>
        </ChannelPartnerLayout></ChannelPartnerRoute>
      } />

      {/* SP4 — CP notifications page (mirrors the dev portal's /notifications
          but rendered inside ChannelPartnerLayout so the bell + sidebar stay
          visible). Same NotificationsPage component — the page is org-agnostic. */}
      <Route path="/partner/notifications" element={
        <ChannelPartnerRoute><ChannelPartnerLayout>
          <Suspense fallback={<LoadingFallback />}><NotificationsPage /></Suspense>
        </ChannelPartnerLayout></ChannelPartnerRoute>
      } />

      {/* SP5 — CP analytics + insights pages */}
      <Route path="/partner/insights" element={
        <ChannelPartnerRoute><ChannelPartnerLayout>
          <Suspense fallback={<LoadingFallback />}><CpInsightsPage /></Suspense>
        </ChannelPartnerLayout></ChannelPartnerRoute>
      } />
      <Route path="/partner/commission" element={
        <ChannelPartnerRoute><ChannelPartnerLayout>
          <Suspense fallback={<LoadingFallback />}><CommissionDashboardPage /></Suspense>
        </ChannelPartnerLayout></ChannelPartnerRoute>
      } />
      <Route path="/partner/commission/reconciliation" element={
        <ChannelPartnerRoute><ChannelPartnerLayout>
          <Suspense fallback={<LoadingFallback />}><ReconciliationDashboardPage /></Suspense>
        </ChannelPartnerLayout></ChannelPartnerRoute>
      } />
      <Route path="/partner/developers/performance" element={
        <ChannelPartnerRoute><ChannelPartnerLayout>
          <Suspense fallback={<LoadingFallback />}><DeveloperPerformanceDetailPage /></Suspense>
        </ChannelPartnerLayout></ChannelPartnerRoute>
      } />

      {/* SP5 — dev-side analytics pages (Areas 6–8) */}
      <Route path="/analytics/intelligence" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Reading your register..." />}>
              <SalesIntelligencePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/analytics/channel-partners" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}><ChannelPartnerScorecardPage /></Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/analytics/commission-payouts" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}><CommissionPayoutsPage /></Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/analytics/lead-quality" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}><LeadQualityPage /></Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* PROJECT MANAGEMENT ROUTES - UNCHANGED */}
      {/* ========================================= */}

      <Route path="/projects" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.viewAllProjects()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <ProjectsListPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/projects/create" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.projectManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <CreateProjectPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/projects/:projectId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.viewAllProjects()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <ProjectDetailPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects/:projectId/edit" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.projectManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading project editor..." />}>
              <EditProjectPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects/:projectId/towers/:towerId/edit" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.projectManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading tower editor..." />}>
              <EditTowerPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects/:projectId/towers/:towerId/units/:unitId/edit" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.projectManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading unit editor..." />}>
              <EditUnitPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects/:projectId/units/:unitId/edit" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.projectManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading villa editor..." />}>
              <EditUnitPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects/:projectId/units/create" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.projectManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <CreateUnitPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects/:projectId/units/:unitId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.viewAllProjects()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <UnitDetailPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects/:projectId/towers/create" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.projectManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <CreateTowerPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects/:projectId/towers/:towerId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.viewAllProjects()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <TowerDetailPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects/:projectId/towers/:towerId/units/create" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.projectManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <CreateUnitPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/projects/:projectId/towers/:towerId/units/:unitId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.viewAllProjects()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <UnitDetailPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* LEAD MANAGEMENT ROUTES - UNCHANGED */}
      {/* ========================================= */}

      <Route path="/leads" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.leadManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <LeadsListPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/leads/create" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.leadManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <CreateLeadPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      {/* SP4 — CP-pushed lead registrations queue (must be before /leads/:leadId) */}
      <Route path="/leads/registrations" element={
        <ProtectedRoute requiredPermission="leads:view">
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <LeadRegistrationsPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/leads/:leadId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.leadManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <LeadDetailPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/leads/:leadId/edit" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.leadManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading lead editor..." />}>
              <EditLeadPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/leads/pipeline" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.leadManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <LeadsPipelinePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* SALES MANAGEMENT ROUTES - UNCHANGED */}
      {/* ========================================= */}

      <Route path="/sales" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesPipeline()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <SalesListPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      {/* /sales/create is a sales action — gated on salesPipeline() only.
          LeadDetailPage's "Convert to Booking" CTA is gated on the SAME
          predicate (salesPipeline()), so the route and the CTA agree: a user
          who sees the button can always reach the page (and CreateSalePage's
          canCreateSales guard uses the same predicate). */}
      <Route path="/sales/create" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesPipeline()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <CreateSalePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/sales/:saleId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesPipeline()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <SaleDetailPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/sales/:saleId/edit" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesPipeline()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading sale editor..." />}>
              <EditSalePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Invoice Management Routes - UNCHANGED */}
      <Route path="/sales/invoices" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesPipeline() || canAccess.viewFinancials()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading invoices..." />}>
              <InvoiceListPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/sales/invoices/generate/:saleId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesPipeline()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading invoice generator..." />}>
              <GenerateInvoicePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/sales/invoices/:invoiceId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesPipeline() || canAccess.viewFinancials()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading invoice details..." />}>
              <InvoiceDetailPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Payment Plan Management Routes - UNCHANGED */}
      <Route path="/sales/payment-plans" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesPipeline()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading payment plan templates..." />}>
              <PaymentPlanManagementPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/payments/plans/:saleId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesPipeline()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading payment plan..." />}>
              <PaymentPlanPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Sales Pipeline & Reports Routes - UNCHANGED */}
      <Route path="/sales/pipeline" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesPipeline()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading sales pipeline..." />}>
              <SalesPipelinePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/sales/reports" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesReports()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading sales reports..." />}>
              <SalesReportsPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* PAYMENT MANAGEMENT ROUTES - UNCHANGED */}
      {/* ========================================= */}

      <Route path="/payments/dashboard" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesPipeline() || canAccess.viewFinancials()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading payment dashboard..." />}>
              <PaymentDashboardPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/payments" element={<Navigate to="/payments/dashboard" replace />} />

      <Route path="/payments/due-today" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesPipeline()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading due payments..." />}>
              <DueTodayPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/payments/overdue" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesPipeline()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading overdue payments..." />}>
              <OverduePaymentsPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/payments/collections" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.viewFinancials()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading collection performance..." />}>
              <CollectionPerformancePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/payments/reports" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesReports()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading payment reports..." />}>
              <PaymentReportsPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/payments/record" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesPipeline()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading payment recorder..." />}>
              <RecordPaymentPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/payments/plans/:saleId/record-payment" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesPipeline()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading payment recorder..." />}>
              <RecordPaymentPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* ANALYTICS ROUTES - ENHANCED FOR PHASE 1 */}
      {/* ========================================= */}

      {/* EXISTING Analytics Routes - UNCHANGED */}
      <Route path="/analytics" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.projectManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback section="analytics" />}>
              <AnalyticsDashboard />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/analytics/sales" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.salesReports()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback section="analytics" />}>
              <SalesAnalytics />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/analytics/revenue" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.viewFinancials()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback section="analytics" />}>
              <RevenueAnalytics />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/analytics/leads" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.leadManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback section="analytics" />}>
              <LeadAnalytics />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* NEW: Phase 1 Enhanced Analytics Routes */}
      
      {/* Budget vs Actual Dashboard - NEW for Phase 1 */}
      <Route path="/analytics/budget" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.viewFinancials()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback section="budget" message="Loading budget analysis..." />}>
              <BudgetVsActualDashboard />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Real-Time Financial Dashboard - NEW for Phase 1 */}
      <Route path="/analytics/financial" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.viewFinancials()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback section="realtime" message="Loading real-time financial data..." />}>
              <RealTimeFinancialDashboard />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* 🆕 NEW: Budget Variance Dashboard - Real-time variance tracking */}
<Route path="/analytics/budget-variance" element={
  <ProtectedRoute requiredPermission={(canAccess) => canAccess.viewFinancials()}>
    <DashboardLayout>
      <Suspense fallback={<LoadingFallback section="budget" message="Loading budget variance dashboard..." />}>
        <BudgetVarianceDashboard />
      </Suspense>
    </DashboardLayout>
  </ProtectedRoute>
} />

      {/* Leadership Dashboard */}
      <Route path="/analytics/leadership" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.projectManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback section="analytics" message="Loading leadership dashboard..." />}>
              <LeadershipDashboardPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Reports — Leadership Report Builder */}
      <Route path="/reports" element={
        <ProtectedRoute requiredPermission="reports:view">
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <ReportTemplateListPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/reports/templates/new" element={
        <ProtectedRoute requiredPermission="reports:manage">
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <ReportAgentPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/reports/templates/:id/edit" element={
        <ProtectedRoute requiredPermission="reports:manage">
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <ReportAgentPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/reports/generated" element={
        <ProtectedRoute requiredPermission="reports:view">
          <DashboardLayout><Suspense fallback={<LoadingFallback />}><ReportInstanceListPage /></Suspense></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/reports/generated/:id/review" element={
        <ProtectedRoute requiredPermission="reports:view">
          <DashboardLayout><Suspense fallback={<LoadingFallback />}><ReportReviewPage /></Suspense></DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/reports/generated/:id" element={
        <ProtectedRoute requiredPermission="reports:view">
          <DashboardLayout><Suspense fallback={<LoadingFallback />}><ReportInstanceAnalyticsPage /></Suspense></DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Enhanced Budget Analysis Routes - NEW for Phase 1 */}
      <Route path="/analytics/budget/variance" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.viewFinancials()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback section="budget" message="Loading variance analysis..." />}>
              <BudgetVsActualDashboard />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/analytics/budget/projects" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.viewFinancials()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback section="budget" message="Loading project budget analysis..." />}>
              <BudgetVsActualDashboard />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* PHASE 2 - PREDICTIVE ANALYTICS & PRICING */}
      {/* ========================================= */}

      {/* Predictive Analytics Page */}
      <Route path="/analytics/predictions" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.projectManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback section="analytics" message="Loading predictive analytics..." />}>
              <PredictiveAnalyticsPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* Dynamic Pricing Page */}
      <Route path="/pricing/dynamic" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.editPricing()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading dynamic pricing..." />}>
              <DynamicPricingPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* PROFILE AND SETTINGS ROUTES - UNCHANGED */}
      {/* ========================================= */}

      <Route path="/profile" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <ProfilePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/settings" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.systemSettings()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <SettingsPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      
      <Route path="/settings/users" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.userManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <UserManagementPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/settings/support" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <SupportSettingsPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/settings/import-data" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading import..." />}>
              <ImportDataPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/settings/voice" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <VoiceAgentSettingsPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/settings/project-access" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.projectManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <ProjectAccessPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* NOTIFICATION ROUTES */}
      {/* ========================================= */}

      <Route path="/notifications" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <NotificationsPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/settings/notifications" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <NotificationPreferencesPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* ROLE MANAGEMENT ROUTES */}
      {/* ========================================= */}

      <Route path="/roles" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.systemSettings()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <RolesListPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/roles/create" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.systemSettings()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <CreateEditRolePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/roles/:roleId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.systemSettings()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <RoleDetailPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/roles/:roleId/edit" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.systemSettings()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <CreateEditRolePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/org-hierarchy" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <OrgHierarchyPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* TASK MANAGEMENT ROUTES */}
      {/* ========================================= */}

      <Route path="/tasks" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.taskManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <MyTasksPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/tasks/all" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.taskManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <TaskListPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/tasks/kanban" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.taskManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <TaskKanbanPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/tasks/create" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.taskManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <CreateEditTaskPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/tasks/team" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.taskTeamView()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <TeamTasksPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/tasks/analytics" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.taskAnalytics()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback section="analytics" />}>
              <TaskAnalyticsPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/tasks/templates" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.taskManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <TaskTemplatesPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/tasks/templates/create" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.taskTemplates()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <CreateEditTemplatePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/tasks/templates/:templateId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.taskTemplates()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <CreateEditTemplatePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/tasks/:taskId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.taskManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <TaskDetailPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/tasks/:taskId/edit" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.taskManagement()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <CreateEditTaskPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* APPROVAL MANAGEMENT ROUTES */}
      {/* ========================================= */}

      <Route path="/approvals" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.approvalsView()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading approvals..." />}>
              <ApprovalsDashboardPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/approvals/policies" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.approvalsPolicies()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading policies..." />}>
              <ApprovalPoliciesPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      <Route path="/approvals/:approvalId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.approvalsView()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading approval details..." />}>
              <ApprovalDetailPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* CHAT ROUTES */}
      {/* ========================================= */}

      <Route path="/chat" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.chatAccess()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading chat..." />}>
              <ChatPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/chat/:conversationId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.chatAccess()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading chat..." />}>
              <ChatPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* CHANNEL PARTNER ROUTES */}
      {/* ========================================= */}
      <Route path="/channel-partners" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.channelPartners()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading channel partners..." />}>
              <ChannelPartnerListPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/channel-partners/create" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.channelPartners()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading form..." />}>
              <ChannelPartnerFormPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/channel-partners/commission-rules" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.channelPartners()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading commission rules..." />}>
              <CommissionRuleListPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/channel-partners/commission-rules/create" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.channelPartners()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading form..." />}>
              <CommissionRuleFormPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/channel-partners/commission-rules/:id" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.channelPartners()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading rule..." />}>
              <CommissionRuleFormPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/channel-partners/commission-records" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.channelPartners()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading commission records..." />}>
              <CommissionRecordListPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/channel-partners/dashboard" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.channelPartners()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading dashboard..." />}>
              <ChannelPartnerDashboardPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/channel-partners/requests" element={
        <ProtectedRoute requiredPermission="channel_partners:view">
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading partnerships..." />}>
              <DeveloperPartnershipsPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/channel-partners/:id" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.channelPartners()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading partner..." />}>
              <ChannelPartnerFormPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* PORTFOLIO ROUTES */}
      {/* ========================================= */}
      <Route path="/portfolio/profile" element={
        <ProtectedRoute requiredPermission="portfolio:manage">
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <PortfolioProfilePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/portfolio/preview" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback />}>
              <PortfolioPreviewPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* COMPETITIVE ANALYSIS ROUTES */}
      {/* ========================================= */}

      <Route path="/competitive-analysis" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.compAnalysisView()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading competitive analysis..." />}>
              <CADashboardPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/competitive-analysis/scorecard" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.compAnalysisView()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading competitive performance..." />}>
              <CompetitivePerformancePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/competitive-analysis/scorecard/:projectId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.compAnalysisView()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading competitive performance..." />}>
              <CompetitivePerformancePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/competitive-analysis/competitors" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.compAnalysisView()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading competitors..." />}>
              <CompetitorListPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/competitive-analysis/competitors/new" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.compAnalysisManageData()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading form..." />}>
              <CompetitorFormPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/competitive-analysis/competitors/:id" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.compAnalysisView()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading competitor details..." />}>
              <CompetitorDetailPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/competitive-analysis/research" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.compAnalysisAIResearch()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading AI Research..." />}>
              <AIResearchPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/competitive-analysis/import" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.compAnalysisManageData()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading import..." />}>
              <CSVImportPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/competitive-analysis/market/overview" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.compAnalysisView()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading market overview..." />}>
              <MarketOverviewPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/competitive-analysis/market/trends" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.compAnalysisView()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading market trends..." />}>
              <MarketTrendsPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/competitive-analysis/market/demand" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.compAnalysisView()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading demand-supply analysis..." />}>
              <DemandSupplyPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/competitive-analysis/analysis" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.compAnalysisAIRecommendations()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading AI analysis..." />}>
              <AIAnalysisPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/competitive-analysis/analysis/:projectId" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.compAnalysisAIRecommendations()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading AI analysis..." />}>
              <AIAnalysisPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/competitive-analysis/settings/providers" element={
        <ProtectedRoute requiredPermission={(canAccess) => canAccess.compAnalysisManageProviders()}>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading data providers..." />}>
              <DataProvidersPage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />

      {/* ========================================= */}
      {/* ERROR ROUTES - UNCHANGED */}
      {/* ========================================= */}

      <Route path="/unauthorized" element={
        <Suspense fallback={<LoadingFallback />}>
          <UnauthorizedPage />
        </Suspense>
      } />
      <Route path="/404" element={
        <Suspense fallback={<LoadingFallback />}>
          <NotFoundPage />
        </Suspense>
      } />

      {/* ========================================= */}
      {/* REDIRECT ROUTES - UNCHANGED */}
      {/* ========================================= */}

      <Route path="/" element={
        <ProtectedRoute>
          <DashboardLayout>
            <Suspense fallback={<LoadingFallback message="Loading home..." />}>
              <HomePage />
            </Suspense>
          </DashboardLayout>
        </ProtectedRoute>
      } />
      <Route path="/home" element={<Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/404" replace />} />
    </Routes>
  );
};

// =============================================================================
// MAIN APP COMPONENT - UNCHANGED
// =============================================================================

const App = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <SnackbarProvider 
        maxSnack={3}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        autoHideDuration={5000}
      >
        <AuthProvider>
          <ProjectProvider>
            <WorkspaceProvider>
              <ChatProvider>
                <CoachMarkProvider flows={FLOWS}>
                  <Router>
                    <Suspense fallback={<LoadingFallback message="Loading PropVantage AI..." />}>
                      <AppRoutes />
                    </Suspense>
                  </Router>
                </CoachMarkProvider>
              </ChatProvider>
            </WorkspaceProvider>
          </ProjectProvider>
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

export default App;